import { useState } from "react";

const photoLocations = [
  { name: "CVS Pharmacy — Apache Junction", address: "1010 W Apache Trail, Apache Junction, AZ 85120", hours: "Daily 7 AM – 10 PM" },
  { name: "Walgreens — Apache Junction", address: "2165 W Apache Trail, Apache Junction, AZ 85120", hours: "Daily 8 AM – 10 PM" },
  { name: "Walgreens — Gold Canyon", address: "5765 S Kings Ranch Rd, Gold Canyon, AZ 85118", hours: "Daily 8 AM – 10 PM" },
  { name: "CVS Pharmacy — Mesa East", address: "7355 E McKellips Rd, Mesa, AZ 85207", hours: "Daily 7 AM – 10 PM" },
  { name: "The UPS Store — Mesa Gateway Area", address: "6900 E Williams Field Rd, Mesa, AZ 85212", hours: "Call for hours" },
  { name: "FedEx Office — Mesa", address: "6617 E Southern Ave, Mesa, AZ 85206", hours: "Mon–Fri 8 AM – 8 PM, Sat 9 AM – 6 PM" },
];

const dmvLocations = [
  {
    name: "MVD Now — Apache Junction",
    address: "1640 S Ironwood Dr, Apache Junction, AZ 85120",
    hours: "Mon–Fri 8:00 AM – 5:00 PM",
    note: "Arizona MVD services including driver licenses, REAL ID, and vehicle registration. Private MVD office — no state appointment needed.",
    featured: true,
  },
  {
    name: "Mesa MVD Office",
    address: "1840 E. Elliot Rd, Mesa, AZ 85284",
    hours: "Mon–Fri 8:00 AM – 5:00 PM",
    note: "State DMV option for East Valley residents. Appointment recommended via AZ MVD Now app.",
    featured: false,
  },
];

export default function ApacheJunctionHelpfulResourcesSection() {
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const visiblePhotos = photoExpanded ? photoLocations : photoLocations.slice(0, 4);

  return (
    <section id="local-resources" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Helpful Resources for Apache Junction Travelers
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Getting your passport ready involves more than just the application. Here are local Pinal County and East Valley resources to help you gather everything you need before your next snowbird season or international trip.
          </p>
        </div>

        <div className="space-y-6">
          {/* Passport Photos */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-camera-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Passport Photos</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Passport Photo Locations Near Apache Junction</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-4">
                Your passport application requires two identical 2&quot;&times;2&quot; passport photos taken within the last 6 months. Popular passport photo locations near Apache Junction include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {visiblePhotos.map((loc, i) => (
                  <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-3.5">
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{loc.name}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-1">{loc.hours}</p>
                  </div>
                ))}
              </div>
              {!photoExpanded && (
                <button
                  onClick={() => setPhotoExpanded(true)}
                  className="text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs flex items-center gap-1.5 mb-4 cursor-pointer transition-colors"
                >
                  <i className="ri-add-circle-line text-sm"></i>
                  Show all {photoLocations.length} photo locations
                </button>
              )}
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-lightbulb-line text-[#F6C344] mr-1.5"></i>
                  <strong>Tip:</strong> Wear a plain white or off-white top, no glasses, neutral expression. Photos must meet strict U.S. State Department specifications — ask the technician to confirm compliance before you leave.
                </p>
              </div>
            </div>
          </div>

          {/* DMV */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-id-card-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Driver&apos;s License &amp; State ID</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Arizona MVD — Driver&apos;s License &amp; REAL ID Services</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                A valid government-issued photo ID is required for your passport application. Apache Junction residents can use the local MVD Now office or the Mesa MVD for driver&apos;s licenses and REAL ID services.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {dmvLocations.map((loc, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${loc.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug">{loc.name}</p>
                    <p className="text-gray-500 text-xs mt-1">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{loc.hours}</p>
                    <p className="text-[#1A365D] text-xs font-medium mt-1.5 leading-snug">{loc.note}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://www.azdot.gov/motor-vehicles" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>Arizona MVD Official Site
                </a>
              </div>
            </div>
          </div>

          {/* Airports */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Airports</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Airports Serving Apache Junction Travelers</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Apache Junction travelers primarily use Phoenix Sky Harbor (PHX) and Mesa Gateway Airport (AZA). PHX handles the bulk of international routes including direct service to Canada, Mexico, and Europe.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {[
                  {
                    code: "PHX",
                    name: "Phoenix Sky Harbor International Airport",
                    distance: "About 35 miles west via US-60",
                    address: "3400 E Sky Harbor Blvd, Phoenix, AZ 85034",
                    note: "Primary international airport for Apache Junction. Nonstop service to Canada, Mexico, Europe, and major U.S. hubs.",
                    flightUrl: "https://www.skyharbor.com/",
                    flightLabel: "PHX Airport Website",
                    featured: true,
                  },
                  {
                    code: "AZA",
                    name: "Phoenix-Mesa Gateway Airport",
                    distance: "About 15 miles west via US-60",
                    address: "6033 S Sossaman Rd, Mesa, AZ 85212",
                    note: "Closer option for Apache Junction travelers. Budget carriers including Allegiant and Spirit. Limited international routes.",
                    flightUrl: "https://www.phxmesagateway.org/",
                    flightLabel: "Mesa Gateway Website",
                    featured: false,
                  },
                ].map((airport, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${airport.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#1A365D] font-black text-xl leading-none">{airport.code}</span>
                      {airport.featured && (
                        <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Primary</span>
                      )}
                    </div>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{airport.name}</p>
                    <div className="flex items-center gap-1.5 text-xs text-[#F6C344] font-semibold mb-2">
                      <i className="ri-road-map-line"></i>
                      <span>{airport.distance}</span>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed mb-1">{airport.address}</p>
                    <p className="text-gray-400 text-xs leading-relaxed mb-3">{airport.note}</p>
                    <a href={airport.flightUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                      <i className="ri-external-link-line text-sm"></i>{airport.flightLabel}
                    </a>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international flights.</strong> If your departure is within two weeks, standard processing won&apos;t make it in time — <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">expedited processing through Fast Passport Center</a> is your most reliable option.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}